'use client';
import { Flowbite } from 'flowbite-react';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { CustomFlowbiteTheme } from 'flowbite-react';
import { allowDarkMode } from '@/utils/common-helpers';
const customTheme: CustomFlowbiteTheme = {
    button: {
        color: {
            primary: 'bg-blue-500 hover:bg-blue-100',
        },
    },
    navbar: {
        collapse: {
            base: 'w-full lg:block lg:w-auto',
            list: 'flex flex-col lg:mt-0 lg:flex-row md:font-medium bg-white lg:bg-transparent dark:bg-transparent',
            hidden: {
                on: 'hidden',
                off: 'open',
            },
        },
        toggle: {
            base: 'inline-flex items-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 lg:hidden',
            icon: 'h-4 w-4 shrink-0',
        },
        link: {
            base: 'flex lg:block font-medium !text-body !border-b !border-light lg:!border-0 leading-30 lg:leading-80 py-2.5 px-5 lg:py-0 lg:px-[14px] hover:bg-gray-50 !text-textcolor hover:!text-blue dark:!text-light dark:hover:bg-gray-700 dark:!hover:text-white md:hover:bg-transparent  md:dark:hover:bg-transparent md:dark:hover:text-white',
        },
    },
    accordion: {
        root: {
            base: 'divide-y divide-gray-200 border-gray-200 dark:divide-gray-700 dark:border-gray-700',
            flush: {
                off: 'rounded-lg border-0',
                on: 'border-t-0 border-b-0',
            },
        },
        content: {
            base: 'pb-5 pt-1 fs-md px-0 last:rounded-b-lg dark:bg-black first:rounded-t-lg !border-t-0',
        },
        title: {
            arrow: {
                base: 'h-6 w-6 shrink-0',
                open: {
                    off: '',
                    on: 'rotate-180 text-primary',
                },
            },
            base: 'flex w-full items-center justify-between first:rounded-t-lg last:rounded-b-lg py-5 px-0 text-left font-medium text-gray-500 dark:text-gray-400',
            flush: {
                off: 'hover:bg-gray-100 focus:ring-4 focus:ring-gray-200 dark:hover:bg-gray-800 dark:focus:ring-gray-800',
                on: 'bg-transparent dark:bg-transparent',
            },
            heading: 'fs-mdlg text-textcolor dark:text-white',
            open: {
                off: '',
                on: 'text-gray-900 dark:bg-black dark:text-white',
            },
        },
    },
    breadcrumb: {
        item: {
            base: 'group flex items-center py-6',
            chevron: 'mx-1 h-4 w-4 text-secondar group-first:hidden md:mx-2',
            href: {
                off: 'flex items-center fs-md font-regular text-secondary dark:text-gray-400',
                on: 'flex items-center fs-md font-regular text-textcolor hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
            },
            icon: 'mr-2 h-4 w-4',
        },
    },
    dropdown: {
        floating: {
            base: 'z-10 w-fit rounded-10 divide-y divide-gray-100 focus:outline-none',
            item: {
                container: ' py-0',
                base: 'flex items-center justify-start py-2 px-4 text-sm text-gray-700 cursor-pointer w-full hover:bg-gray-100 focus:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600 focus:outline-none dark:hover:text-white dark:focus:bg-gray-600 dark:focus:text-white',
                icon: 'mr-2 h-4 w-4',
            },
        },
        inlineWrapper: 'flex items-center py-5 px-4',
    },
    tab: {
        tablist: {
            base: 'flex text-center w-full justify-center !border-gray-700',
        },
    },
};
const FlowbiteContext: FC<PropsWithChildren> = function ({ children }) {
    const pathname = usePathname();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        // Check if dark mode should be applied based on pathname
        const shouldUseDarkMode = allowDarkMode(pathname);
        setIsDarkMode(shouldUseDarkMode);

        // Also apply to document element to ensure consistency
        if (shouldUseDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [pathname]);

    // Prevent hydration mismatch by not rendering until mounted
    if (!mounted) {
        return <Flowbite theme={{ theme: customTheme }}>{children}</Flowbite>;
    }

    return (
        <Flowbite theme={{ theme: customTheme }} className={isDarkMode ? 'dark' : ''}>
            {children}
        </Flowbite>
    );
};

export default FlowbiteContext;
